import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Box, Stack, Typography, IconButton, Chip, Paper, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { pickServiceName } from '../../utils/serviceName';
import dayjs from 'dayjs';

// Історія + статистика клієнта. Один компонент для сторінки «Клієнти» та деталей запису.
export default function ClientHistoryModal({ open, onClose, user, services }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await api.getClientBookings(user.id);
        const now = dayjs();
        const mapped = data.map((b) => {
          const start = dayjs(`${b.date}T${b.time}`);
          let status;
          if (b.src === 'archive') status = b.reason === 'no_show' ? 'no_show' : 'cancelled';
          else status = start.isAfter(now) ? 'upcoming' : 'completed';
          return { ...b, status };
        });
        if (alive) setRows(mapped);
      } catch {
        if (alive) setRows([]);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, user?.id]);

  const stats = useMemo(() => {
    const acc = { total: rows.length, completed: 0, upcoming: 0, cancelled: 0, no_show: 0 };
    for (const b of rows) if (acc[b.status] !== undefined) acc[b.status] += 1;
    return acc;
  }, [rows]);

  const serviceNamesByIds = (ids) =>
    (Array.isArray(ids) ? ids : [])
      .map((id) => (services || []).find((s) => s.id === id))
      .filter(Boolean)
      .map((s) => pickServiceName(s, i18n.language))
      .join(', ');

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: '#faf7f2', color: '#1a1a16',
        border: '1px solid rgba(28,29,23,0.12)', borderRadius: 2,
        boxShadow: 24, p: 3, width: '90%', maxWidth: 500, maxHeight: '85vh', overflowY: 'auto'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{t('bookingDetails.clientDetails')}</Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#1a1a16' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ borderColor: 'rgba(28,29,23,0.18)', mb: 2 }} />

        {user && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography>{t('common.name')}: <b>{user.name}</b></Typography>
            {user.email !== undefined && <Typography>{t('common.email')}: <b>{user.email || '—'}</b></Typography>}
            <Typography>{t('common.phone')}: <b>{user.phone || '—'}</b></Typography>
            <Typography>{t('bookingDetails.status')}: <b>{user.status || '—'}</b></Typography>
          </Stack>
        )}

        {!loading && rows.length > 0 && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip size="small" variant="outlined" label={`${t('bookingDetails.statTotal')}: ${stats.total}`} />
            <Chip size="small" color="success" label={`${t('bookingDetails.statCompleted')}: ${stats.completed}`} />
            {stats.upcoming > 0 && <Chip size="small" color="info" label={`${t('bookingDetails.statUpcoming')}: ${stats.upcoming}`} />}
            <Chip size="small" color="error" label={`${t('bookingDetails.statCancelled')}: ${stats.cancelled}`} />
            <Chip size="small" color="warning" label={`${t('bookingDetails.statNoShow')}: ${stats.no_show}`} />
          </Stack>
        )}

        <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>{t('bookingDetails.bookingsHistory')}:</Typography>
        {loading ? (
          <Typography color="secondary">{t('common.loading')}</Typography>
        ) : (
          <Stack spacing={1}>
            {rows.length === 0 ? (
              <Typography sx={{ color: '#57564d' }}>{t('bookingDetails.noBookingsFound')}</Typography>
            ) : rows.map((b, idx) => {
              const statusColor = { completed: 'success', upcoming: 'info', cancelled: 'error', no_show: 'warning' }[b.status] || 'default';
              const statusI18n = { completed: 'statusCompleted', upcoming: 'statusUpcoming', cancelled: 'statusCancelled', no_show: 'statusNoShow' }[b.status];
              const reasonI18n = { master: 'reasonMaster', client: 'reasonClient', no_show: 'reasonNoShow', other: 'reasonOther' }[b.reason];
              return (
                <Paper key={b.id ?? idx} sx={{ p: 1, bgcolor: '#faf7f2', border: '1px solid rgba(28,29,23,0.18)' }}>
                  <Typography><strong>{t('bookingDetails.date')}:</strong> {b.date} <strong>{t('bookingDetails.time')}:</strong> {b.time}</Typography>
                  <Typography><strong>{t('bookingDetails.services')}:</strong> {serviceNamesByIds(b.services) || '—'}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mt={1} useFlexGap flexWrap="wrap">
                    <Chip size="small" color={statusColor} label={t(`bookingDetails.${statusI18n}`)} />
                    {b.status === 'cancelled' && reasonI18n && (
                      <Typography variant="caption" sx={{ color: '#57564d' }}>
                        {t('bookingDetails.reasonLabel')}: {t(`bookingDetails.${reasonI18n}`)}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    </Modal>
  );
}
